import type { Cols, SizeStep, WidgetLayoutItem } from '../../platform/grid/gridMaths'
import type { Breakpoint } from '../../platform/responsive/useBreakpoint'

export type WidgetId = 'net-worth' | 'upcoming' | 'budgets' | 'spent' | 'where-it-went' | 'recent' | 'cashflow' | 'heatmap' | 'insight'

/** The spec's sizes (M2-overview.md:23-35): width in columns, height in steps. */
// heatmap is 2×3, not the spec's 2×2: at 238px its cells measured 17.8px, under the 18px floor, and a six-week month is smaller.
export const SIZES: Record<WidgetId, { w: Cols; h: SizeStep }> = {
  'net-worth': { w: 2, h: 2 },
  cashflow: { w: 2, h: 3 },
  spent: { w: 1, h: 2 },
  'where-it-went': { w: 2, h: 2 },
  budgets: { w: 2, h: 3 },
  recent: { w: 2, h: 3 },
  upcoming: { w: 2, h: 3 },
  heatmap: { w: 2, h: 3 },
  // insight is 2×3, not the spec's 2×2: the stack (padding 40, head 26, four 12px gaps, title 19-38,
  // body 37-56, the reason it was shown 34-51, strip 32) is 236-291px against a 238px 2×2 cell, and
  // 322px at 2×3. The body scrolls, so extreme copy never clips.
  insight: { w: 2, h: 3 },
}

/**
 * Desktop: the hero, then Upcoming | Budget envelopes directly beneath it, so
 * overdue and over-budget are on screen without scrolling (PRD M2-R4 — the PRD wins
 * over the render's order). Spent is the only one-wide widget, so it goes last: the
 * unavoidable hole trails at the bottom right instead of sitting mid-page.
 */
export const DESKTOP_ORDER: readonly WidgetId[] = [
  'net-worth',
  'upcoming',
  'budgets',
  'insight',
  'heatmap',
  'where-it-went',
  'cashflow',
  'recent',
  'spent',
]

/** Phone (one column): action-first (R15) — what needs doing before the totals. */
export const PHONE_ORDER: readonly WidgetId[] = [
  'upcoming',
  'budgets',
  'spent',
  'recent',
  'insight',
  'net-worth',
  'cashflow',
  'where-it-went',
  'heatmap',
]

/** Fixed per breakpoint — there is no Customise in v1 (intake B1). */
export function orderFor(bp: Breakpoint): readonly WidgetId[] {
  return bp === 'phone' ? PHONE_ORDER : DESKTOP_ORDER
}

export function layoutFor(bp: Breakpoint): WidgetLayoutItem<WidgetId>[] {
  return orderFor(bp).map((id) => ({ id, ...SIZES[id] }))
}
